// src/pages/dashboard/DashboardRektor.tsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, AlertTriangle, Clock,
  BarChart3, ArrowUpRight, Calendar, Sparkles, Shield,
  ChevronRight, Hourglass, Send, Gavel, CalendarDays,
  CalendarRange, Eye, Award, HelpCircle, Info, XCircle, Search
} from 'lucide-react';
import {
  Card, Badge, Breadcrumb, Loading, LogoLoadingMark, Button, Modal, Input, Select,
} from '../../components/ui';
import { DashboardData, TingkatRisiko } from '../../types';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, BarChart, Bar,
} from 'recharts';
import api from '../../services/api';
import { formatDate } from '../../utils/formatDate';
import { useCapabilities } from '../../hooks/useCapabilities';

// ========== CONSTANTS ==========
const RISK_COLORS: Record<string, string> = {
  rendah: '#22C55E', sedang: '#F59E0B',
  tinggi: '#F97316', kritis: '#EF4444',
};

const KATEGORI_COLORS = [
  '#66C9CC', '#47AEC9', '#3D93BD', '#3A6BA6', '#6366F1', '#14B8A6'
];

// Status labels yang user-friendly (BAHASA INDONESIA PENUH)
const STATUS_LABELS_FRIENDLY: Record<string, string> = {
  draf: 'Draf',
  diajukan: 'Menunggu Persetujuan',
  validasi_l1: 'Disetujui Dekan',
  validasi_l2: 'Disetujui Wakil Rektor',
  ditolak_l1: 'Dikembalikan oleh Dekan',
  ditolak_l2: 'Dikembalikan oleh Wakil Rektor',
  antrean_rapim: 'Antrean Rapat Pimpinan',
  diputuskan: 'Keputusan Rapat Pimpinan',
  berlangsung: 'Sedang Dikerjakan',
  menunggu_konfirmasi: 'Menunggu Konfirmasi Selesai',
  selesai: 'Selesai',
  diarsipkan: 'Diarsipkan',
  diselesaikan_lokal: 'Ditangani Langsung oleh Dekan/Kepala Lembaga atau Wakil Rektor',
};

const CHART_COLORS: Record<string, string> = {
  draf: '#94A3B8', diajukan: '#3B82F6',
  validasi_l1: '#06B6D4', validasi_l2: '#A855F7',
  antrean_rapim: '#F97316', diputuskan: '#22C55E',
  berlangsung: '#EAB308', menunggu_konfirmasi: '#F59E0B',
  selesai: '#10B981', ditolak_l1: '#EF4444',
  ditolak_l2: '#DC2626', diarsipkan: '#9CA3AF',
  diselesaikan_lokal: '#0EA5E9',
};

// Alur Status - label user-friendly
const STATUS_FLOW = [
  { key: 'draf', label: 'Draf', icon: FileText, color: '#94A3B8', bgColor: 'bg-slate-100' },
  { key: 'diajukan', label: 'Diajukan', icon: Send, color: '#3B82F6', bgColor: 'bg-blue-100' },
  { key: 'validasi_l1', label: 'Persetujuan Dekan', icon: CheckCircle, color: '#06B6D4', bgColor: 'bg-cyan-100' },
  { key: 'validasi_l2', label: 'Persetujuan Warek', icon: Shield, color: '#A855F7', bgColor: 'bg-purple-100' },
  { key: 'antrean_rapim', label: 'Rapat Pimpinan', icon: Gavel, color: '#F97316', bgColor: 'bg-orange-100' },
  { key: 'berlangsung', label: 'Dikerjakan', icon: Clock, color: '#EAB308', bgColor: 'bg-yellow-100' },
  { key: 'menunggu_konfirmasi', label: 'Konfirmasi', icon: Hourglass, color: '#F59E0B', bgColor: 'bg-amber-100' },
  { key: 'selesai', label: 'Selesai', icon: CheckCircle, color: '#10B981', bgColor: 'bg-emerald-100' },
  { key: 'diselesaikan_lokal', label: 'Ditangani Langsung', icon: Award, color: '#0EA5E9', bgColor: 'bg-sky-100' },
];

// Trend Granularity
type TrendGranularity = 'harian' | 'mingguan' | 'bulanan';

const TREND_OPTIONS: { id: TrendGranularity; label: string; icon: React.ReactNode }[] = [
  { id: 'harian', label: 'Harian', icon: <CalendarDays className="w-3.5 h-3.5" /> },
  { id: 'mingguan', label: 'Mingguan', icon: <CalendarRange className="w-3.5 h-3.5" /> },
  { id: 'bulanan', label: 'Bulanan', icon: <Calendar className="w-3.5 h-3.5" /> },
];

interface TrendDataItem {
  label: string;
  laporan_masuk: number;
  laporan_selesai: number;
  selesai_rapim: number;
  ditangani_langsung: number;
  dalam_proses: number;
}

interface AuditReport {
  id: number;
  kode: string;
  judul: string;
  status: string;
  tingkat_risiko?: string;
  resolution_level?: string | null;
  unit?: { name?: string | null } | null;
  user?: { name?: string | null } | null;
  target_efektif?: string | null;
  target_selesai?: string | null;
  created_at?: string | null;
}

interface AuditMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface KpiCard {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  bgIcon: string;
  subtitle: string;
  pulse: boolean;
  onClick?: () => void;
  helperLinkLabel?: string;
}

const defaultData: DashboardData = {
  total_laporan: 0,
  laporan_per_status: {
    draf: 0, diajukan: 0, validasi_l1: 0, ditolak_l1: 0,
    validasi_l2: 0, ditolak_l2: 0, antrean_rapim: 0,
    diputuskan: 0, tugas_ppepp_aktif: 0, berlangsung: 0,
    menunggu_konfirmasi: 0, selesai: 0, diarsipkan: 0,
    diselesaikan_lokal: 0,
  },
  resolved_local_count: 0,
  resolved_local_list: [],
  laporan_overdue: 0,
  rata_rata_penyelesaian: 0,
  laporan_per_risiko: { rendah: 0, sedang: 0, tinggi: 0, kritis: 0 },
  progress_ppepp: 0,
  tren_bulanan: [],
  laporan_per_kategori: [],
  rapim_terdekat: [],
  laporan_terbaru: [],
  overdue_list: [],
  hambatan_aktif_count: 0,
  hambatan_aktif_list: [],
  menunggu_konfirmasi_list: [],
};

// ========== HELPER FUNCTIONS ==========
const toRiskLevel = (risk: string | undefined): TingkatRisiko => {
  const validRisks: TingkatRisiko[] = ['rendah', 'sedang', 'tinggi', 'kritis'];
  const normalized = (risk || 'sedang').toLowerCase() as TingkatRisiko;
  return validRisks.includes(normalized) ? normalized : 'sedang';
};

// Custom tooltip untuk chart - Bahasa Indonesia Detail
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white border border-neutral-border rounded-xl shadow-lg p-3 text-xs min-w-[200px]">
      <p className="font-bold text-neutral-text-primary mb-2 border-b pb-1">{label}</p>
      <div className="space-y-1.5">
        {payload.map((entry: any, i: number) => {
          let labelStr = entry.name;
          if (entry.name === 'laporan_masuk') labelStr = 'Laporan Masuk';
          if (entry.name === 'selesai_rapim') labelStr = 'Selesai via RAPIM';
          if (entry.name === 'ditangani_langsung') labelStr = 'Ditangani Langsung';
          if (entry.name === 'dalam_proses') labelStr = 'Masih dlm Proses';

          return (
            <div key={i} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-neutral-text-secondary">{labelStr}</span>
              </div>
              <span className="font-bold text-neutral-text-primary">{entry.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const formatResolutionLabel = (level?: string | null): string | null => {
  if (!level) return null;

  const labels: Record<string, string> = {
    universitas: 'Level Universitas',
    fakultas: 'Level Fakultas',
    lembaga: 'Level Lembaga',
    prodi: 'Level Program Studi',
    unit: 'Level Unit',
    lokal: 'Level Lokal',
  };

  return labels[level] || level.replace(/_/g, ' ');
};

// ========== COMPONENT ==========
const DashboardRektor: React.FC = () => {
  const caps = useCapabilities();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData>(defaultData);
  const [antreanRapim, setAntreanRapim] = useState<any[]>([]);
  const [rapimTerdekatFallback, setRapimTerdekatFallback] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stuckCount, setStuckCount] = useState(0);
  const [showGuide, setShowGuide] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditModalTitle, setAuditModalTitle] = useState('Semua Laporan');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditReports, setAuditReports] = useState<AuditReport[]>([]);
  const [auditMeta, setAuditMeta] = useState<AuditMeta | null>(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditSearchDraft, setAuditSearchDraft] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditStatusFilter, setAuditStatusFilter] = useState('');

  // Trend States
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>('bulanan');
  const [trendData, setTrendData] = useState<TrendDataItem[]>([]);
  const [trendLoading, setTrendLoading] = useState(false);

  const [periodeList, setPeriodeList] = useState<any[]>([]);
  const [selectedPeriode, setSelectedPeriode] = useState<string>('');
  const [isPeriodeReady, setIsPeriodeReady] = useState(false);

  const normalizeOverdueDays = (value: unknown): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.round(Math.abs(parsed)));
  };

  const normalizeTrendData = (rawData: any[], granularity: TrendGranularity): TrendDataItem[] => {
    if (!Array.isArray(rawData)) return [];
    return rawData.map((item) => ({
      label: item.label || item.bulan || '-',
      laporan_masuk: Number(item.laporan_masuk || item.jumlah || 0),
      laporan_selesai: Number(item.laporan_selesai || item.selesai || 0),
      selesai_rapim: Number(item.selesai_rapim || 0),
      ditangani_langsung: Number(item.ditangani_langsung || 0),
      dalam_proses: Number(item.dalam_proses || 0),
    }));
  };

  // ========== FETCH PERIODS ==========
  useEffect(() => {
    const fetchPeriode = async () => {
      try {
        const periodeRes = await api.get('/admin/periode').catch(() => ({ data: { data: [] } }));
        const periodeData = periodeRes.data?.data || periodeRes.data || [];
        const normalizedPeriode = Array.isArray(periodeData) ? periodeData : [];
        setPeriodeList(normalizedPeriode);
        const activePeriode = normalizedPeriode.find((p: any) => p?.is_active);
        setSelectedPeriode(activePeriode ? String(activePeriode.id) : '');
      } catch {
        setPeriodeList([]);
        setSelectedPeriode('');
      } finally {
        setIsPeriodeReady(true);
      }
    };
    fetchPeriode();
  }, []);

  // ========== FETCH DASHBOARD DATA ==========
  useEffect(() => {
    if (!isPeriodeReady) return; // Tunggu periode siap

    const fetchDashboard = async () => {
      try {
        setLoading(true);

        const params: Record<string, any> = {};
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const [dashboardRes, rapimRes, rapatRes] = await Promise.all([
          api.get('/dashboard', { params }),
          api.get('/rapim/available-laporan', { params: { per_page: 5, ...params } })
            .catch(() => api.get('/laporan', { params: { status: 'antrean_rapim', per_page: 5, ...params } })
              .catch(() => ({ data: { data: [] } }))),
          api.get('/rapim', { params: { per_page: 20 } })
            .catch(() => ({ data: { data: [] } })),
        ]);

        // ... rest of the implementation remains same but using filtered data
        const dashData = dashboardRes.data?.data || dashboardRes.data;
        if (dashData) {
          setData({ ...defaultData, ...dashData });
          setStuckCount(Number(dashData.stuck_validations_count) || 0);
          const bulananData = dashData.tren_bulanan || [];
          setTrendData(normalizeTrendData(bulananData, 'bulanan'));
        } else {
          setStuckCount(0);
        }

        const rapimData = rapimRes.data?.data || rapimRes.data || [];
        setAntreanRapim(Array.isArray(rapimData) ? rapimData : []);

        const rapatPayload = rapatRes.data?.data || rapatRes.data || [];
        const rapatItems = Array.isArray(rapatPayload) ? rapatPayload : (rapatPayload?.data || []);

        const normalizedRapat = rapatItems
          .map((item: any) => ({
            id: item.id,
            judul: item.judul,
            tanggal: item.tanggal,
            tempat: item.tempat,
            status: item.status,
            jumlah_peserta: item.jumlah_peserta ?? item.peserta_count ?? 0,
            jumlah_laporan: item.jumlah_laporan ?? item.laporan_masalah_count ?? 0,
          }))
          .filter((item: any) =>
            !!item.id && !!item.tanggal &&
            ['dijadwalkan', 'berlangsung'].includes(String(item.status || '').toLowerCase())
          )
          .sort((a: any, b: any) => new Date(a.tanggal).getTime() - new Date(b.tanggal).getTime());

        setRapimTerdekatFallback(normalizedRapat);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
        setData(defaultData);
        setStuckCount(0);
        setRapimTerdekatFallback([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [isPeriodeReady, selectedPeriode]);

  useEffect(() => {
    if (!showAuditModal) return;

    let cancelled = false;

    const fetchAuditReports = async () => {
      try {
        setAuditLoading(true);

        const params: Record<string, any> = {
          page: auditPage,
          per_page: 8,
        };

        if (selectedPeriode) params.periode_id = selectedPeriode;
        if (auditStatusFilter) params.status = auditStatusFilter;
        if (auditSearch.trim()) params.search = auditSearch.trim();

        const res = await api.get('/laporan', { params });
        if (cancelled) return;

        const payload = res.data || {};
        const items = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.data?.data)
            ? payload.data.data
            : [];
        const meta = payload.meta || payload.data?.meta || null;

        setAuditReports(items);
        setAuditMeta(meta);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load rector audit reports:', err);
          setAuditReports([]);
          setAuditMeta(null);
        }
      } finally {
        if (!cancelled) {
          setAuditLoading(false);
        }
      }
    };

    fetchAuditReports();

    return () => {
      cancelled = true;
    };
  }, [showAuditModal, auditPage, auditSearch, auditStatusFilter, selectedPeriode]);

  // Fetch trend data when granularity changes
  useEffect(() => {
    const fetchTrendData = async () => {
      if (trendGranularity === 'bulanan' && data.tren_bulanan && data.tren_bulanan.length > 0) {
        setTrendData(normalizeTrendData(data.tren_bulanan, 'bulanan'));
        return;
      }
      setTrendLoading(true);
      try {
        const params: Record<string, any> = { granularity: trendGranularity };
        if (selectedPeriode) params.periode_id = selectedPeriode;

        const res = await api.get('/monitoring/trend', { params });
        const rawTrend = res.data?.data || res.data || [];
        setTrendData(normalizeTrendData(rawTrend, trendGranularity));
      } catch (err) {
        console.error('Failed to fetch trend data:', err);
        if (data.tren_bulanan) {
          setTrendData(normalizeTrendData(data.tren_bulanan, 'bulanan'));
        }
      } finally {
        setTrendLoading(false);
      }
    };

    if (!loading) {
      fetchTrendData();
    }
  }, [trendGranularity, loading, data.tren_bulanan, selectedPeriode]);

  // ========== SMART INSIGHTS ==========
  const generateInsights = () => {
    const overdueCount = data.laporan_overdue || 0;
    const selesaiCount = (data.laporan_per_status?.selesai || 0) + ((data.laporan_per_status as any)?.diarsipkan || 0);
    const totalCount = data.total_laporan || 0;
    const menungguKonfirmasi = (data.laporan_per_status as any)?.menunggu_konfirmasi || 0;
    const resolvedLocal = data.resolved_local_count || 0;
    const hambatanAktif = data.hambatan_aktif_count || 0;

    const parts: string[] = [];

    if (menungguKonfirmasi > 0) {
      parts.push(`${menungguKonfirmasi} laporan menunggu konfirmasi penyelesaian dari Anda.`);
    }
    if (hambatanAktif > 0) {
      parts.push(`${hambatanAktif} tindak lanjut sedang terhambat dan memerlukan keputusan pimpinan.`);
    }
    if (overdueCount > 0) {
      parts.push(`${overdueCount} laporan sudah melewati batas waktu dan perlu perhatian segera.`);
    } else {
      parts.push('Semua laporan masih dalam batas waktu yang ditetapkan.');
    }
    if (resolvedLocal > 0) {
      parts.push(`${resolvedLocal} laporan telah ditangani langsung oleh Dekan/Kepala Lembaga atau Wakil Rektor.`);
    }
    if (totalCount > 0) {
      const completionRate = Math.round(((selesaiCount + resolvedLocal) / totalCount) * 100);
      parts.push(`Tingkat penyelesaian keseluruhan saat ini mencapai ${completionRate}%.`);
    }

    return parts.join(' ');
  };

  const getTrendTitle = () => {
    switch (trendGranularity) {
      case 'harian': return 'Pergerakan Laporan 30 Hari Terakhir';
      case 'mingguan': return 'Pergerakan Laporan 12 Minggu Terakhir';
      case 'bulanan': default: return 'Pergerakan Laporan 6 Bulan Terakhir';
    }
  };

  // ========== COMPUTED DATA ==========
  const totalSelesaiFinal = (data.laporan_per_status?.selesai || 0)
    + ((data.laporan_per_status as any)?.diarsipkan || 0);
  const resolvedLocalCount = data.resolved_local_count || 0;
  const totalTuntas = totalSelesaiFinal + resolvedLocalCount;
  const menungguKonfirmasiCount = (data.laporan_per_status as any)?.['menunggu_konfirmasi'] || 0;
  const menungguKonfirmasiList = data.menunggu_konfirmasi_list || [];
  const hambatanAktifCount = data.hambatan_aktif_count || 0;
  const hambatanAktifList = data.hambatan_aktif_list || [];

  const completionRate = data.total_laporan > 0
    ? Math.round((totalTuntas / data.total_laporan) * 100)
    : 0;

  // KPI Cards - Bahasa Indonesia penuh
  const kpiCards: KpiCard[] = [
    {
      title: 'Total Laporan',
      value: data.total_laporan,
      icon: <FileText className="w-5 h-5" />,
      color: 'text-teal-main',
      bgIcon: 'bg-teal-main/10',
      subtitle: 'Seluruh laporan yang masuk',
      pulse: false,
    },
    {
      title: 'Tuntas',
      value: totalTuntas,
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'text-success',
      bgIcon: 'bg-success/10',
      subtitle: `${totalSelesaiFinal} via Rapat + ${resolvedLocalCount} ditangani langsung`,
      pulse: false,
    },
    {
      title: 'Ditangani Langsung',
      value: resolvedLocalCount,
      icon: <Award className="w-5 h-5" />,
      color: 'text-emerald-600',
      bgIcon: 'bg-emerald-100',
      subtitle: 'Selesai tanpa Rapat Pimpinan',
      pulse: false,
    },
    {
      title: 'Antrean Rapat Pimpinan',
      value: antreanRapim.length,
      icon: <Gavel className="w-5 h-5" />,
      color: 'text-purple-600',
      bgIcon: 'bg-purple-100',
      subtitle: 'Perlu dijadwalkan rapat',
      pulse: antreanRapim.length > 0,
    },
    {
      title: 'Menunggu Konfirmasi Anda',
      value: menungguKonfirmasiCount,
      icon: <Shield className="w-5 h-5" />,
      color: 'text-amber-700',
      bgIcon: 'bg-amber-100',
      subtitle: 'Perlu Anda setujui sebagai selesai',
      pulse: menungguKonfirmasiCount > 0,
      onClick: menungguKonfirmasiCount > 0
        ? () => navigate('/monitoring?ppepp_tab=menunggu_konfirmasi&open=ppepp')
        : undefined,
      helperLinkLabel: menungguKonfirmasiCount > 0 ? 'Lihat antrean' : undefined,
    },
    {
      title: 'Melewati Batas Waktu',
      value: data.laporan_overdue,
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'text-danger',
      bgIcon: 'bg-danger/10',
      subtitle: 'Perlu perhatian segera',
      pulse: data.laporan_overdue > 0,
    },
    {
      title: 'Rata-rata Penyelesaian',
      value: `${data.rata_rata_penyelesaian} hari`,
      icon: <Clock className="w-5 h-5" />,
      color: 'text-info',
      bgIcon: 'bg-info/10',
      subtitle: 'Durasi dari pengajuan sampai selesai',
      pulse: false,
    },
  ];

  // Pie data - menggunakan label user-friendly
  const riskPieData = Object.entries(data.laporan_per_risiko).map(([key, value]) => ({
    name: key === 'kritis' ? 'Kritis' : key.charAt(0).toUpperCase() + key.slice(1),
    value,
    fill: RISK_COLORS[key] || '#6B7280',
  }));

  const statusPieData = Object.entries(data.laporan_per_status)
    .filter(([key]) => key !== 'tugas_ppepp_aktif')
    .filter(([, value]) => value > 0)
    .map(([key, value]) => ({
      name: STATUS_LABELS_FRIENDLY[key] || key,
      value,
      fill: CHART_COLORS[key] || '#6B7280',
    }));

  const kategoriChartData = ((data as any).laporan_per_kategori || []).map((item: any, idx: number) => ({
    name: item.kategori,
    jumlah: item.jumlah,
    fill: item.color || KATEGORI_COLORS[idx % KATEGORI_COLORS.length],
  }));

  const rapimItems = (
    (data as any).rapim_terdekat?.length
      ? (data as any).rapim_terdekat
      : rapimTerdekatFallback
  ) || [];

  const formatRapimDate = (value?: string | null) => {
    if (!value) return '-';
    const dt = new Date(value);
    return dt.toLocaleString('id-ID', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };
  const selectedPeriodeLabel = selectedPeriode
    ? periodeList.find((periode) => String(periode.id) === selectedPeriode)?.nama || 'Periode dipilih'
    : 'Semua Periode';
  const auditStatusOptions = [
    { value: '', label: 'Semua Status' },
    { value: 'diajukan', label: 'Menunggu Persetujuan' },
    { value: 'validasi_l1', label: 'Persetujuan Dekan' },
    { value: 'validasi_l2', label: 'Persetujuan Warek' },
    { value: 'antrean_rapim', label: 'Antrean RAPIM' },
    { value: 'diputuskan', label: 'Sudah Diputuskan' },
    { value: 'berlangsung', label: 'Sedang Dikerjakan' },
    { value: 'menunggu_konfirmasi', label: 'Menunggu Konfirmasi' },
    { value: 'diselesaikan_lokal', label: 'Ditangani Langsung' },
    { value: 'selesai', label: 'Selesai' },
    { value: 'diarsipkan', label: 'Diarsipkan' },
  ];
  const openAuditModal = (status = '', title = 'Semua Laporan') => {
    setAuditModalTitle(title);
    setAuditPage(1);
    setAuditSearchDraft('');
    setAuditSearch('');
    setAuditStatusFilter(status);
    setShowAuditModal(true);
  };

  // ========== LOADING ==========
  if (loading) {
    return <Loading variant="page" message="Memuat data dashboard..." />;
  }

  // ========== RENDER ==========
  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: 'Rektor' }]} />

      {/* ===== HERO BANNER ===== */}
      <div className="relative overflow-hidden rounded-2xl bg-teal-dark p-6 md:p-8 text-white shadow-lg">
        <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full bg-white opacity-5" />
        <div className="absolute bottom-0 right-24 -mb-16 w-40 h-40 rounded-full bg-teal-400 opacity-10 blur-2xl" />
        <div className="relative z-10">
          <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-2">
            Dashboard Eksekutif
          </h1>
          <p className="text-teal-100 text-sm leading-relaxed mb-4">
            Ringkasan kondisi penyelesaian masalah dan kemajuan evaluasi universitas secara menyeluruh.
          </p>

          {/* Smart Insight */}
          <div className="inline-flex items-start gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 shadow-sm">
            <div className="p-1.5 bg-amber-400/20 rounded-lg shrink-0">
              <Sparkles className="w-4 h-4 text-amber-300" />
            </div>
            <div>
              <h4 className="text-xs font-semibold text-white mb-0.5">Ringkasan Kondisi</h4>
              <p className="text-[11px] text-teal-50 leading-relaxed">
                {generateInsights()}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 text-xs text-teal-200">
              <Calendar className="w-4 h-4" />
              <span>Periode:</span>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                icon={<HelpCircle className="w-4 h-4" />}
                className="border-white/20 bg-white/10 text-white hover:bg-white/15 hover:text-white focus:ring-white/30"
                onClick={() => setShowGuide((prev) => !prev)}
              >
                Panduan
              </Button>
              <select
                value={selectedPeriode}
                onChange={(e) => setSelectedPeriode(e.target.value)}
                className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 w-full sm:w-auto"
              >
                <option value="" className="text-gray-900">Semua Periode</option>
                {periodeList.map((p) => (
                  <option key={p.id} value={String(p.id)} className="text-gray-900">
                    {p.nama}{p.is_active ? ' (Aktif)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tingkat Penyelesaian Bar */}
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-teal-200">Tingkat Penyelesaian Keseluruhan</span>
              <span className="text-sm font-black text-white">{completionRate}%</span>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-300 transition-all duration-500"
                style={{ width: `${completionRate}%` }}
              />
            </div>
            <p className="text-[10px] text-teal-300 mt-1">
              {totalTuntas} dari {data.total_laporan} laporan sudah dituntaskan
              ({totalSelesaiFinal} melalui Rapat Pimpinan, {resolvedLocalCount} ditangani langsung pimpinan)
            </p>
          </div>
        </div>
      </div>

      {showGuide && (
        <Card className="border border-teal-main/10 bg-gradient-to-r from-white via-white to-teal-main/5 animate-in">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-teal-main/10 shrink-0">
                <Info className="w-5 h-5 text-teal-main" />
              </div>
              <div>
                <h2 className="text-base font-bold text-teal-dark">Cara membaca dashboard Rektor</h2>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Ringkasan ini membantu membaca dashboard eksekutif secara cepat: mana laporan yang sudah tuntas, mana yang masih menunggu keputusan pimpinan, dan mana yang perlu perhatian segera.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              className="text-neutral-text-secondary hover:text-teal-main transition-colors shrink-0"
              aria-label="Tutup panduan dashboard Rektor"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Tuntas</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menghitung seluruh laporan yang telah ditutup, baik selesai melalui jalur Rapat Pimpinan maupun yang sudah diarsipkan setelah proses dinyatakan final.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Ditangani Langsung</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan laporan yang diselesaikan oleh Dekan, Kepala Lembaga, atau Wakil Rektor tanpa perlu pembahasan di Rapat Pimpinan. Angka ini dipisahkan agar jalurnya tidak tertukar dengan jalur RAPIM.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Antrean Rapat Pimpinan</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Berisi laporan yang sudah disetujui Wakil Rektor dan siap dijadwalkan ke Rapat Pimpinan untuk penetapan keputusan tindak lanjut universitas.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Menunggu Konfirmasi Anda</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menandakan tindak lanjut telah mencapai progres 100% dan menunggu pengesahan akhir dari pimpinan yang berwenang. Buka detail tindak lanjut dari antrean konfirmasi untuk meninjau hasil kerja PIC, lalu setujui atau tolak pengajuan selesai.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Melewati Batas Waktu</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menunjukkan laporan yang melampaui target waktu penyelesaian, sehingga dapat digunakan sebagai indikator prioritas eskalasi dan pengawasan.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Hambatan Aktif</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Menampilkan tindak lanjut yang sedang terhambat dan menunggu keputusan pimpinan, seperti perpanjangan target, pergantian PIC, atau pengembalian ke Rapat Pimpinan.
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/80 border border-neutral-border">
              <p className="text-xs font-bold text-neutral-text-primary">Tingkat Penyelesaian & Grafik</p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Persentase di hero banner menghitung total laporan tuntas dibanding seluruh laporan. Grafik tren dan sebaran di bawahnya membantu melihat pergerakan laporan masuk, penyelesaian via RAPIM, penanganan langsung, dan distribusi status per tahap.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* ===== ALERT RAPIM ===== */}
      {antreanRapim.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3 shadow-sm">
          <div className="p-2 bg-purple-100 rounded-lg shrink-0">
            <Gavel className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-purple-900">
              Ada Laporan Menunggu Rapat Pimpinan
            </h3>
            <p className="text-sm text-purple-800 mt-1">
              Terdapat <strong>{antreanRapim.length} laporan</strong> yang sudah disetujui
              Wakil Rektor dan perlu segera dijadwalkan dalam Rapat Pimpinan untuk ditetapkan
              keputusan tindak lanjutnya.
            </p>
          </div>
          {caps.canManageRapim && (
            <Link to="/rapim">
              <button className="whitespace-nowrap px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                Jadwalkan Rapat
              </button>
            </Link>
          )}
        </div>
      )}

      {stuckCount > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl border-2 bg-amber-50 border-amber-200">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-sm text-amber-800">
              Validasi Terhambat
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Terdapat <strong>{stuckCount} laporan</strong> yang menunggu validasi
              lebih dari 3 hari tanpa tindakan. Anda dapat melakukan override dari
              halaman detail laporan.
            </p>
          </div>
          <Link to="/laporan?status=diajukan">
            <button className="whitespace-nowrap px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
              Tinjau
            </button>
          </Link>
        </div>
      )}

      {menungguKonfirmasiCount > 0 && (
        <div
          id="pending-confirmation-section"
          className="rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-amber-50/40 p-4 shadow-sm"
        >
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                <Shield className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-amber-900">
                  Ada Tindak Lanjut Menunggu Konfirmasi Selesai
                </h3>
                <p className="text-sm text-amber-800 mt-1">
                  Terdapat <strong>{menungguKonfirmasiCount} tindak lanjut</strong> yang sudah mencapai progres 100% dan menunggu persetujuan akhir dari Anda. Buka detail tindak lanjut untuk meninjau hasil kerja PIC, lalu pilih terima atau tolak pengajuan selesai.
                </p>
              </div>
            </div>
            <Link to="/monitoring?ppepp_tab=menunggu_konfirmasi&open=ppepp" className="shrink-0">
              <button className="whitespace-nowrap px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                Buka Monitoring
              </button>
            </Link>
          </div>

          {menungguKonfirmasiList.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mt-4">
              {menungguKonfirmasiList.slice(0, 3).map((item) => {
                const tanggalDiajukan = item.diajukan_pada
                  ? new Date(item.diajukan_pada).toLocaleDateString('id-ID', {
                    day: 'numeric',
                    month: 'short',
                  })
                  : 'Baru diajukan';

                return (
                  <Link
                    key={item.rekomendasi_id}
                    to={`/tindak-lanjut/${item.rekomendasi_id}`}
                    className="group rounded-xl border border-amber-200 bg-white/90 p-3 hover:border-amber-300 hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                            {item.kode}
                          </span>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">
                            Menunggu Konfirmasi
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-neutral-text-primary truncate group-hover:text-amber-700">
                          {item.judul}
                        </p>
                        <p className="text-[11px] text-neutral-text-secondary mt-1">
                          PIC: <span className="font-semibold text-neutral-text-primary">{item.pic}</span>
                        </p>
                        <p className="text-[11px] text-neutral-text-secondary">
                          Unit: {item.unit}
                        </p>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-neutral-text-secondary opacity-0 group-hover:opacity-100 shrink-0" />
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-border">
                      <span className="text-[10px] text-neutral-text-secondary">
                        Diajukan {tanggalDiajukan}
                      </span>
                      <span className="text-[10px] font-semibold text-amber-700">
                        Tinjau & Konfirmasi
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {hambatanAktifCount > 0 && (
        <div className="rounded-2xl border border-red-200 bg-gradient-to-r from-red-50 via-white to-red-50/40 p-4 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-red-900">
                  Ada Tindak Lanjut yang Membutuhkan Keputusan
                </h3>
                <p className="text-sm text-red-800 mt-1">
                  Terdapat <strong>{hambatanAktifCount} tindak lanjut</strong> yang sedang mengalami hambatan. Anda dapat meninjau hambatan yang dilaporkan dan menentukan langkah lanjutan yang diperlukan.
                </p>
              </div>
            </div>
            <Link to="/monitoring" className="shrink-0">
              <button className="whitespace-nowrap px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm">
                Buka Monitoring
              </button>
            </Link>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mt-4">
            {hambatanAktifList.slice(0, 3).map((item) => {
              const tanggalDilaporkan = item.dilaporkan_pada
                ? new Date(item.dilaporkan_pada).toLocaleDateString('id-ID', {
                  day: 'numeric',
                  month: 'short',
                })
                : 'Baru dilaporkan';

              return (
                <Link
                  key={item.rekomendasi_id}
                  to={`/tindak-lanjut/${item.rekomendasi_id}`}
                  className="group rounded-xl border border-red-200 bg-white/90 p-3 hover:border-red-300 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">
                          {item.kode}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                          {item.jenis_hambatan}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-neutral-text-primary truncate group-hover:text-red-700">
                        {item.judul}
                      </p>
                      <p className="text-[11px] text-neutral-text-secondary mt-1">
                        PIC: <span className="font-semibold text-neutral-text-primary">{item.pic}</span>
                      </p>
                      <p className="text-[11px] text-neutral-text-secondary">
                        Unit: {item.unit}
                      </p>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-neutral-text-secondary opacity-0 group-hover:opacity-100 shrink-0" />
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-border">
                    <span className="text-[10px] text-neutral-text-secondary">
                      Dilaporkan {tanggalDilaporkan}
                    </span>
                    <span className="text-[10px] font-semibold text-red-700">
                      Progress {item.progress_percentage}%
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== KPI CARDS ===== */}
      <div className="flex flex-wrap lg:grid lg:grid-cols-7 gap-3">
        {kpiCards.map((kpi, i) => (
          <div
            key={i}
            className={`flex-1 min-w-[140px] bg-white rounded-xl border border-neutral-border p-4 shadow-sm transition-all relative overflow-hidden group ${kpi.onClick ? 'hover:shadow-md hover:border-amber-200' : 'hover:shadow-md'
              }`}
          >
            {/* Pulse indicator */}
            {kpi.pulse && (
              <div className="absolute top-3 right-3">
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-danger opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-danger" />
                </span>
              </div>
            )}

            {kpi.onClick ? (
              <button
                type="button"
                onClick={kpi.onClick}
                className="w-full text-left"
                aria-label={`Buka antrean ${kpi.title}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className={`p-2 rounded-lg ${kpi.bgIcon} ${kpi.color}`}>
                    {kpi.icon}
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-text-primary tracking-tight">
                    {kpi.value}
                  </p>
                  <h3 className="text-xs font-bold text-neutral-text-secondary mt-0.5">
                    {kpi.title}
                  </h3>
                  <p className="text-[9px] text-neutral-border-dark mt-0.5 line-clamp-2">
                    {kpi.subtitle}
                  </p>
                  {kpi.helperLinkLabel && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700">
                      {kpi.helperLinkLabel}
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  )}
                </div>
              </button>
            ) : (
              <>
                <div className="flex justify-between items-start mb-3">
                  <div className={`p-2 rounded-lg ${kpi.bgIcon} ${kpi.color}`}>
                    {kpi.icon}
                  </div>
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-text-primary tracking-tight">
                    {kpi.value}
                  </p>
                  <h3 className="text-xs font-bold text-neutral-text-secondary mt-0.5">
                    {kpi.title}
                  </h3>
                  <p className="text-[9px] text-neutral-border-dark mt-0.5 line-clamp-2">
                    {kpi.subtitle}
                  </p>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* ===== TREN LAPORAN + RISIKO ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tren Chart */}
        <Card className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-teal-dark">{getTrendTitle()}</h3>
              {trendLoading && (
                <LogoLoadingMark size="xs" />
              )}
            </div>

            {/* Toggle Periode */}
            <div className="flex items-center gap-1 bg-neutral-bg rounded-lg p-1">
              {TREND_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setTrendGranularity(option.id)}
                  disabled={trendLoading}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${trendGranularity === option.id
                    ? 'bg-white text-teal-main shadow-sm'
                    : 'text-neutral-text-secondary hover:text-teal-main hover:bg-white/50'
                    } ${trendLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {option.icon}
                  <span className="hidden sm:inline">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Chart */}
          {trendData.length > 0 ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                {trendGranularity === 'harian' ? (
                  <BarChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 9, fill: '#6B7280' }}
                      interval={2}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="circle"
                      wrapperStyle={{ fontSize: '10px', paddingBottom: '20px' }}
                      formatter={(value) => {
                        if (value === 'laporan_masuk') return 'Masuk';
                        if (value === 'selesai_rapim') return 'Selesai RAPIM';
                        if (value === 'ditangani_langsung') return 'Ditangani Langsung';
                        if (value === 'dalam_proses') return 'Dalam Proses';
                        return value;
                      }}
                    />
                    <Bar
                      dataKey="selesai_rapim"
                      stackId="a"
                      fill="#10B981"
                      name="selesai_rapim"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="ditangani_langsung"
                      stackId="a"
                      fill="#06B6D4"
                      name="ditangani_langsung"
                      radius={[0, 0, 0, 0]}
                    />
                    <Bar
                      dataKey="dalam_proses"
                      stackId="a"
                      fill="#F59E0B"
                      name="dalam_proses"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar
                      dataKey="laporan_masuk"
                      fill="#0f33ab"
                      name="laporan_masuk"
                      radius={[2, 2, 0, 0]}
                      barSize={12}
                    />
                  </BarChart>
                ) : (
                  <LineChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6B7280' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend
                      verticalAlign="top"
                      align="right"
                      iconType="circle"
                      wrapperStyle={{ fontSize: '10px', paddingBottom: '20px' }}
                      formatter={(value) => {
                        if (value === 'laporan_masuk') return 'Masuk';
                        if (value === 'selesai_rapim') return 'Selesai RAPIM';
                        if (value === 'ditangani_langsung') return 'Ditangani Langsung';
                        if (value === 'dalam_proses') return 'Dalam Proses';
                        return value;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="laporan_masuk"
                      stroke="#0f33ab"
                      strokeWidth={3}
                      dot={{ fill: '#0f33ab', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="laporan_masuk"
                    />
                    <Line
                      type="monotone"
                      dataKey="selesai_rapim"
                      stroke="#10B981"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#10B981', r: 3 }}
                      name="selesai_rapim"
                    />
                    <Line
                      type="monotone"
                      dataKey="ditangani_langsung"
                      stroke="#06B6D4"
                      strokeWidth={2}
                      strokeDasharray="3 3"
                      dot={{ fill: '#06B6D4', r: 3 }}
                      name="ditangani_langsung"
                    />
                    <Line
                      type="monotone"
                      dataKey="dalam_proses"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      dot={{ fill: '#F59E0B', r: 3 }}
                      name="dalam_proses"
                    />
                  </LineChart>
                )}
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-neutral-text-secondary italic text-sm">
              {trendLoading ? 'Memuat data pergerakan...' : 'Belum ada data tersedia untuk periode ini'}
            </div>
          )}

          {/* Ringkasan Performa di Bawah Chart */}
          {trendData.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-neutral-border">
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-text-secondary">Total Masuk</span>
                <p className="text-lg font-black text-teal-dark">
                  {trendData.reduce((sum, d) => sum + d.laporan_masuk, 0)}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-success">Selesai RAPIM</span>
                <p className="text-lg font-black text-success">
                  {trendData.reduce((sum, d) => sum + d.selesai_rapim, 0)}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-cyan-600">Ditangani Langsung</span>
                <p className="text-lg font-black text-cyan-600">
                  {trendData.reduce((sum, d) => sum + d.ditangani_langsung, 0)}
                </p>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider font-bold text-warning">Sedang Berjalan</span>
                <p className="text-lg font-black text-warning">
                  {trendData.reduce((sum, d) => sum + d.dalam_proses, 0)}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Distribusi Risiko */}
        <Card>
          <h3 className="text-base font-bold text-teal-dark mb-3">Sebaran Tingkat Risiko</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={riskPieData} cx="50%" cy="50%"
                innerRadius={55} outerRadius={90} paddingAngle={4}
                dataKey="value"
              >
                {riskPieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => [`${value} laporan`, '']}
                contentStyle={{
                  backgroundColor: '#fff', border: '1px solid #E5E7EB',
                  borderRadius: '12px', fontSize: '11px',
                }}
              />
              <Legend
                verticalAlign="bottom" iconSize={8}
                formatter={(value: string) => (
                  <span className="text-[10px] text-neutral-text-secondary">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ===== ALUR STATUS + DISTRIBUSI + KATEGORI ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Kolom Kiri */}
        <div className="space-y-6">
          {/* Alur Status Laporan */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-teal-dark">Alur dan Jalur Penyelesaian Saat Ini</h3>
              <button
                type="button"
                onClick={() => openAuditModal('', 'Semua Laporan Periode Aktif')}
                className="text-xs text-teal-main hover:underline"
              >
                Lihat Semua
              </button>
            </div>
            <p className="text-xs text-neutral-text-secondary mb-4">
              Jumlah laporan pada setiap tahapan utama, termasuk jalur penyelesaian langsung di tingkat Dekan, Kepala Lembaga, atau Wakil Rektor
            </p>
            <div className="grid grid-cols-4 gap-1.5">
              {STATUS_FLOW.map((status, index) => {
                const count = status.key === 'selesai'
                  ? totalSelesaiFinal
                  : ((data.laporan_per_status as any)?.[status.key] || 0);
                const IconComponent = status.icon;
                const isActive = count > 0;
                const isLast = index === STATUS_FLOW.length - 1;

                return (
                  <div
                    key={status.key}
                    className={`flex flex-col items-center p-1.5 rounded-lg border transition-all ${isActive
                      ? 'bg-white border-neutral-border/50 shadow-sm'
                      : 'bg-neutral-bg/30 border-transparent'
                      } ${isLast && isActive ? 'ring-1 ring-success/30' : ''}`}
                  >
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center mb-0.5 ${isActive ? status.bgColor : 'bg-neutral-bg'
                        }`}
                      style={{ color: isActive ? status.color : '#9CA3AF' }}
                    >
                      <IconComponent className="w-3 h-3" />
                    </div>
                    <p
                      className="text-sm font-black leading-none"
                      style={{ color: isActive ? status.color : '#D1D5DB' }}
                    >
                      {count}
                    </p>
                    <p className={`text-[7px] font-medium text-center leading-tight mt-0.5 ${isActive ? 'text-neutral-text-secondary' : 'text-neutral-text-secondary/50'
                      }`}>
                      {status.label}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Ringkasan bawah */}
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-neutral-border">
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-neutral-text-secondary">
                  <span className="font-bold text-warning">
                    {(data.laporan_per_status?.berlangsung || 0) + ((data.laporan_per_status as any)?.menunggu_konfirmasi || 0)}
                  </span> sedang dikerjakan
                </span>
                <span className="text-[10px] text-neutral-text-secondary">
                  <span className="font-bold text-success">{totalTuntas}</span> tuntas
                </span>
              </div>
              <span className="text-[10px] font-bold text-teal-main">{completionRate}%</span>
            </div>
          </Card>

          {/* Distribusi Status (Donut) */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-teal-dark">Sebaran Status Laporan</h3>
              <span className="text-xs text-neutral-text-secondary">
                {statusPieData.reduce((sum, item) => sum + item.value, 0)} laporan aktif
              </span>
            </div>
            {statusPieData.length > 0 ? (
              <div className="flex items-center gap-4">
                <div className="w-1/2">
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={statusPieData} cx="50%" cy="50%"
                        innerRadius={40} outerRadius={70}
                        paddingAngle={3} dataKey="value"
                      >
                        {statusPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [`${value ?? 0} laporan`, '']}
                        contentStyle={{
                          backgroundColor: '#fff', border: '1px solid #E5E7EB',
                          borderRadius: '8px', fontSize: '10px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 grid grid-cols-1 gap-1">
                  {statusPieData.slice(0, 6).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 p-1 rounded bg-neutral-bg/50">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.fill }} />
                      <span className="text-[8px] text-neutral-text-secondary flex-1 truncate">
                        {item.name}
                      </span>
                      <span className="text-xs font-bold text-neutral-text-primary">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-neutral-text-secondary text-sm">
                Belum ada data
              </div>
            )}
          </Card>
        </div>

        {/* Kolom Kanan */}
        <div className="space-y-6">
          {/* Laporan per Kategori */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-teal-dark">Laporan per Bidang Masalah</h3>
              <BarChart3 className="w-4 h-4 text-neutral-text-secondary" />
            </div>
            {kategoriChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie
                    data={kategoriChartData} dataKey="jumlah" nameKey="name"
                    cx="32%" cy="50%" outerRadius={70}
                    stroke="#FFFFFF" strokeWidth={2}
                  >
                    {kategoriChartData.map((entry: any, index: number) => (
                      <Cell key={`cat-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: any) => [`${value ?? 0} laporan`, '']} />
                  <Legend
                    layout="vertical" align="right" verticalAlign="middle"
                    iconType="circle" iconSize={6}
                    formatter={(value: string) => (
                      <span className="text-[8px] text-neutral-text-primary">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex items-center justify-center text-neutral-text-secondary text-sm">
                Belum ada data bidang masalah
              </div>
            )}
          </Card>

          {/* Laporan Melewati Batas Waktu */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                </div>
                <h3 className="text-base font-bold text-teal-dark">Melewati Batas Waktu</h3>
              </div>
              <Badge variant="risk" risk="kritis">{data.laporan_overdue}</Badge>
            </div>
            <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
              {(data.overdue_list && data.overdue_list.length > 0) ? (
                data.overdue_list.slice(0, 4).map((item: any, i: number) => (
                  <Link
                    key={i}
                    to={`/laporan/${item.id}`}
                    className="flex items-center gap-2 p-2 rounded-lg bg-red-50/50 border border-red-100 hover:bg-red-50 transition-colors group"
                  >
                    <div className="text-center shrink-0 w-8">
                      <p className="text-base font-black text-danger leading-none">
                        {normalizeOverdueDays(item.hari_terlambat ?? item.hari)}
                      </p>
                      <p className="text-[7px] text-danger font-semibold">hari</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 mb-0.5">
                        <span className="text-[9px] font-mono text-teal-main">{item.kode}</span>
                        <Badge variant="risk" risk={toRiskLevel(item.risiko || item.tingkat_risiko)} />
                      </div>
                      <p className="text-[10px] font-medium text-neutral-text-primary truncate">
                        {item.judul}
                      </p>
                    </div>
                    <ArrowUpRight className="w-3 h-3 text-neutral-text-secondary opacity-0 group-hover:opacity-100 shrink-0" />
                  </Link>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center mb-1.5">
                    <CheckCircle className="w-4 h-4 text-success" />
                  </div>
                  <p className="text-[10px] font-bold text-neutral-text-primary">
                    Semua laporan masih dalam batas waktu!
                  </p>
                </div>
              )}
            </div>
            {data.laporan_overdue > 4 && (
              <Link
                to="/laporan?status=overdue"
                className="mt-2 pt-2 border-t border-neutral-border text-[10px] text-teal-main hover:underline flex items-center gap-0.5 justify-center"
              >
                Lihat semua {data.laporan_overdue} laporan terlambat
                <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </Card>
        </div>
      </div>

      {/* ===== DITANGANI LANGSUNG PIMPINAN (pengganti "Selesai Lokal") ===== */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-success/10 rounded-lg">
              <Award className="w-4 h-4 text-success" />
            </div>
            <div>
              <h3 className="text-base font-bold text-teal-dark">
                Laporan Diselesaikan di Tingkat Pimpinan Fakultas/Unit
              </h3>
              <p className="text-[10px] text-neutral-text-secondary">
                Laporan yang diselesaikan oleh Dekan atau Wakil Rektor tanpa perlu pembahasan di Rapat Pimpinan
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {resolvedLocalCount > 0 && (
              <button
                type="button"
                onClick={() => openAuditModal('diselesaikan_lokal', 'Laporan Ditangani Langsung')}
                className="text-xs text-success font-semibold hover:underline"
              >
                Lihat Semua
              </button>
            )}
            <span className="px-2.5 py-1 text-xs inline-flex items-center font-bold rounded-full bg-success text-white">
              {resolvedLocalCount}
            </span>
          </div>
        </div>

        {(data.resolved_local_list && data.resolved_local_list.length > 0) ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.resolved_local_list.slice(0, 4).map((item: any, i: number) => (
              <Link
                key={i}
                to={`/laporan/${item.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-success/5 border border-success/20 hover:bg-success/10 transition-all group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-mono text-teal-main">{item.kode}</span>
                    <Badge variant="risk" risk={toRiskLevel(item.tingkat_risiko || item.risiko)} />
                  </div>
                  <p className="text-sm font-medium text-neutral-text-primary truncate">{item.judul}</p>
                  <p className="text-[10px] text-neutral-text-secondary mt-0.5">
                    Ditangani oleh: <span className="font-semibold">{item.resolved_by || '-'}</span>
                    {' • '}
                    Bidang: {item.kategori || '-'}
                  </p>
                </div>
                <ArrowUpRight className="w-3.5 h-3.5 text-neutral-text-secondary opacity-0 group-hover:opacity-100 shrink-0" />
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 border border-dashed border-neutral-border rounded-xl">
            <Award className="w-8 h-8 text-neutral-text-secondary opacity-40 mb-2" />
            <p className="text-sm text-neutral-text-secondary">
              Belum ada laporan yang ditangani langsung
            </p>
            <p className="text-[10px] text-neutral-text-secondary mt-1">
              Laporan akan muncul ketika Dekan atau Wakil Rektor memilih menyelesaikan masalah di tingkatnya
            </p>
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-neutral-border">
          <p className="text-[10px] text-neutral-text-secondary text-center">
            Buka <span className="font-semibold text-success">Lihat Semua</span> untuk melihat daftar laporan yang lebih lengkap.
          </p>
        </div>
      </Card>

      {/* ===== ANTREAN RAPAT PIMPINAN ===== */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-teal-dark flex items-center gap-2">
            <Gavel className="w-5 h-5 text-purple-600" />
            Laporan Menunggu Rapat Pimpinan
          </h3>
          {caps.canManageRapim ? (
            <Link to="/rapim" className="text-xs font-semibold text-teal-main hover:underline flex items-center gap-1">
              Kelola Rapat <ChevronRight className="w-3 h-3" />
            </Link>
          ) : (
            <span className="text-xs text-neutral-text-secondary">Hanya lihat</span>
          )}
        </div>
        <p className="text-xs text-neutral-text-secondary mb-3">
          Laporan berikut sudah disetujui oleh Wakil Rektor dan siap untuk dibahas dalam Rapat Pimpinan.
        </p>
        <div className="space-y-2">
          {antreanRapim.length > 0 ? (
            antreanRapim.map((lap, i) => (
              <div key={lap.id || i} className="flex items-center gap-3 p-3 rounded-xl border border-purple-200 bg-purple-50/50 hover:bg-purple-50 transition-colors">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Gavel className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-mono text-teal-main">{lap.kode}</span>
                    <Badge variant="risk" risk={(lap.tingkat_risiko || lap.risiko || 'sedang') as TingkatRisiko} />
                  </div>
                  <p className="text-sm font-medium truncate">{lap.judul}</p>
                  <p className="text-xs text-neutral-text-secondary">
                    {lap.unit?.name || '-'} • Diajukan: {formatDate(lap.created_at)}
                  </p>
                </div>
                <Link to={`/laporan/${lap.id}`}>
                  <button className="p-2 rounded-lg hover:bg-purple-100 text-purple-600 transition-colors">
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Gavel className="w-8 h-8 mx-auto mb-2 text-neutral-text-secondary opacity-40" />
              <p className="text-sm text-neutral-text-secondary">
                Tidak ada laporan yang menunggu rapat saat ini
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1">
                Laporan akan muncul setelah disetujui Wakil Rektor
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* ===== RAPAT PIMPINAN TERDEKAT ===== */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-teal-dark">Jadwal Rapat Pimpinan Terdekat</h3>
          <Link to="/rapim" className="text-xs text-teal-main hover:underline">Lihat Semua</Link>
        </div>
        {rapimItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rapimItems.slice(0, 3).map((item: any) => (
              <Link
                key={item.id}
                to={`/rapim/${item.id}`}
                className="flex items-start gap-3 p-3 rounded-xl border border-neutral-border hover:border-teal-main/30 transition-all"
              >
                <div className="w-9 h-9 rounded-lg bg-info/10 text-info flex items-center justify-center shrink-0">
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-text-primary truncate">{item.judul}</p>
                  <p className="text-[10px] text-neutral-text-secondary mt-0.5">
                    {formatRapimDate(item.tanggal)}
                  </p>
                  <div className="flex gap-1.5 mt-1">
                    <span className="text-[9px] px-1.5 py-0.5 bg-neutral-bg rounded">
                      {item.jumlah_laporan ?? 0} laporan
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded ${item.status === 'berlangsung'
                      ? 'bg-success/10 text-success'
                      : 'bg-info/10 text-info'
                      }`}>
                      {item.status === 'berlangsung' ? 'Sedang Berlangsung' : 'Dijadwalkan'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 bg-neutral-bg/30 rounded-xl border border-dashed border-neutral-border">
            <p className="text-xs text-neutral-text-secondary">
              Belum ada jadwal Rapat Pimpinan mendatang
            </p>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showAuditModal}
        onClose={() => setShowAuditModal(false)}
        title={auditModalTitle}
        size="2xl"
        footer={(
          <Button variant="ghost" onClick={() => setShowAuditModal(false)}>
            Tutup
          </Button>
        )}
      >
        <div className="space-y-5">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-teal-dark">
                Daftar laporan untuk periode {selectedPeriodeLabel}
              </p>
              <p className="text-xs text-neutral-text-secondary mt-1 leading-relaxed">
                Anda bisa melihat semua laporan pada periode ini. Gunakan pencarian atau pilih status untuk menemukan laporan yang ingin dilihat.
              </p>
            </div>
            <Badge className="self-start" size="sm">
              {auditMeta?.total ?? 0} laporan
            </Badge>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Total Periode
              </p>
              <p className="text-xl font-black text-teal-main mt-1">{data.total_laporan}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Tuntas via RAPIM
              </p>
              <p className="text-xl font-black text-success mt-1">{totalSelesaiFinal}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Ditangani Langsung
              </p>
              <p className="text-xl font-black text-emerald-600 mt-1">{resolvedLocalCount}</p>
            </div>
            <div className="p-3 rounded-xl bg-neutral-bg border border-neutral-border">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">
                Menunggu Konfirmasi
              </p>
              <p className="text-xl font-black text-amber-700 mt-1">{(data.laporan_per_status as any)?.menunggu_konfirmasi || 0}</p>
            </div>
          </div>

          <form
            className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px_auto_auto] gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              setAuditPage(1);
              setAuditSearch(auditSearchDraft.trim());
            }}
          >
            <Input
              placeholder="Cari kode laporan, judul, atau unit"
              value={auditSearchDraft}
              onChange={(event) => setAuditSearchDraft(event.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
            <Select
              options={auditStatusOptions}
              value={auditStatusFilter}
              onChange={(value) => {
                setAuditPage(1);
                setAuditStatusFilter(value);
              }}
            />
            <Button type="submit" variant="outline">
              Cari
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setAuditPage(1);
                setAuditSearchDraft('');
                setAuditSearch('');
                setAuditStatusFilter('');
              }}
            >
              Reset
            </Button>
          </form>

          <div className="rounded-2xl border border-neutral-border overflow-hidden">
            <div className="px-4 py-3 bg-neutral-bg border-b border-neutral-border flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-neutral-text-primary">Daftar Laporan Rektor</p>
                <p className="text-xs text-neutral-text-secondary mt-1">
                  Menyajikan hasil audit sesuai periode dashboard dan filter yang Anda pilih.
                </p>
              </div>
              {auditLoading && (
                <span className="text-xs text-neutral-text-secondary">Memuat data...</span>
              )}
            </div>

            <div className="max-h-[55vh] overflow-y-auto divide-y divide-neutral-border">
              {auditLoading ? (
                <div className="py-12">
                  <Loading message="Memuat laporan..." />
                </div>
              ) : auditReports.length > 0 ? (
                auditReports.map((report) => (
                  <div key={report.id} className="p-4 bg-white hover:bg-neutral-bg/60 transition-colors">
                    <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="text-xs font-mono font-semibold text-teal-main">
                            {report.kode}
                          </span>
                          <Badge
                            variant="status"
                            status={report.status}
                            size="xs"
                            useShortLabel
                          />
                          {report.tingkat_risiko && (
                            <Badge
                              variant="risk"
                              risk={toRiskLevel(report.tingkat_risiko)}
                              size="xs"
                            />
                          )}
                          {formatResolutionLabel(report.resolution_level) && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                              {formatResolutionLabel(report.resolution_level)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-bold text-neutral-text-primary">
                          {report.judul}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-text-secondary">
                          <span>Pelapor: {report.user?.name || '-'}</span>
                          <span>Unit: {report.unit?.name || '-'}</span>
                          <span>Target: {formatDate(report.target_efektif || report.target_selesai || '-') || '-'}</span>
                          <span>Dibuat: {formatDate(report.created_at || '-') || '-'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Link to={`/laporan/${report.id}`}>
                          <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" />}>
                            Lihat Detail
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center">
                  <FileText className="w-10 h-10 mx-auto text-neutral-text-secondary mb-3" />
                  <p className="text-sm font-semibold text-neutral-text-primary">
                    Belum ada laporan yang cocok dengan filter
                  </p>
                  <p className="text-xs text-neutral-text-secondary mt-1">
                    Ubah kata kunci atau status untuk melihat laporan lain dalam periode ini.
                  </p>
                </div>
              )}
            </div>
          </div>

          {auditMeta && auditMeta.last_page > 1 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-neutral-text-secondary">
                Halaman {auditMeta.current_page} dari {auditMeta.last_page}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page <= 1}
                  onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                >
                  Sebelumnya
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditMeta.current_page >= auditMeta.last_page}
                  onClick={() => setAuditPage((prev) => (
                    auditMeta ? Math.min(auditMeta.last_page, prev + 1) : prev + 1
                  ))}
                >
                  Berikutnya
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default DashboardRektor;
